import { flushLogs, logEvent } from "./logger";
import { REDACTED, isSensitiveField, redactUrl } from "./redact";

/**
 * Everything a person does on the page: taps, field values, submits, keys, scrolls,
 * swipes, long presses, the page being hidden, the network dropping, and errors.
 *
 * Listeners are on `document` in the capture phase, so a component that stops
 * propagation still gets logged, and nothing here needs a change in any page.
 *
 * Volume is deliberate: a scroll fires ~60 events a second, so it is logged once per
 * gesture; a text field is logged once, when its value is committed, not per key.
 */
const INTERACTIVE = 'button, a, input, select, textarea, label, summary, [role="button"], [role="tab"], [role="menuitem"], [role="switch"], [role="checkbox"], [role="option"], [data-testid]';
const TEXT_LIMIT = 80;
const SCROLL_IDLE_MS = 250;
const SWIPE_MIN_PX = 60;
const SWIPE_MAX_MS = 800;
const LONG_PRESS_MS = 500;
const HEARTBEAT_MS = 5 * 60 * 1000;

let installed = false;
let uninstall = () => {};

function textOf(element) {
  const raw = element.innerText ?? element.textContent ?? "";
  const text = String(raw).replace(/\s+/g, " ").trim();
  return text.length > TEXT_LIMIT ? `${text.slice(0, TEXT_LIMIT)}…` : text;
}

/** Enough to find the element again: its test id, role, label and visible text. */
export function describeElement(start) {
  const element = start?.closest ? start.closest(INTERACTIVE) || start : start;
  if (!element || !element.tagName) return null;
  const attr = name => element.getAttribute?.(name) || undefined;
  const tag = element.tagName.toLowerCase();
  const isField = tag === "input" || tag === "textarea" || tag === "select";
  return {
    tag,
    testid: attr("data-testid"),
    id: element.id || undefined,
    name: attr("name"),
    role: attr("role"),
    type: attr("type"),
    label: attr("aria-label") || attr("title") || undefined,
    text: isField ? undefined : textOf(element) || undefined,
    href: tag === "a" ? redactUrl(attr("href")) : undefined,
    disabled: element.disabled || attr("aria-disabled") === "true" || undefined,
  };
}

function valueOf(element) {
  const tag = element.tagName?.toLowerCase();
  const type = (element.getAttribute?.("type") || "").toLowerCase();
  if (isSensitiveField(element)) return REDACTED;
  if (type === "checkbox" || type === "radio") return !!element.checked;
  if (tag === "select") {
    const option = element.options?.[element.selectedIndex];
    return option ? { value: option.value, text: option.text } : element.value;
  }
  const value = element.value ?? "";
  return String(value).slice(0, 500);
}

function onClick(event) {
  logEvent("click", {
    target: describeElement(event.target),
    x: Math.round(event.clientX || 0),
    y: Math.round(event.clientY || 0),
  });
}

function onChange(event) {
  const element = event.target;
  if (!element?.tagName) return;
  logEvent("input", { target: describeElement(element), value: valueOf(element) });
}

function onSubmit(event) {
  logEvent("submit", { target: describeElement(event.target) });
}

function onKeyDown(event) {
  if (event.key !== "Enter" && event.key !== "Escape") return;
  logEvent("key", { key: event.key, target: describeElement(event.target) });
}

// ── scroll: once per gesture ────────────────────────────────────────────────────
const scrolls = new Map();

function scrollPosition(target) {
  if (target === document || target === document.documentElement || target === document.body) {
    return { top: window.scrollY, left: window.scrollX, element: document.documentElement };
  }
  return { top: target.scrollTop || 0, left: target.scrollLeft || 0, element: target };
}

function onScroll(event) {
  const { top, left, element } = scrollPosition(event.target);
  let gesture = scrolls.get(element);
  if (!gesture) {
    gesture = { fromTop: top, fromLeft: left, timer: null };
    scrolls.set(element, gesture);
  }
  gesture.toTop = top;
  gesture.toLeft = left;
  clearTimeout(gesture.timer);
  gesture.timer = setTimeout(() => {
    scrolls.delete(element);
    logEvent("scroll", {
      target: element === document.documentElement ? { tag: "page" } : describeElement(element),
      from: { top: Math.round(gesture.fromTop), left: Math.round(gesture.fromLeft) },
      to: { top: Math.round(gesture.toTop), left: Math.round(gesture.toLeft) },
    });
  }, SCROLL_IDLE_MS);
}

// ── touch: swipes and long presses ──────────────────────────────────────────────
let touch = null;

function onTouchStart(event) {
  const point = event.touches?.[0];
  if (!point) return;
  touch = { x: point.clientX, y: point.clientY, at: Date.now(), target: event.target };
}

function onTouchEnd(event) {
  if (!touch) return;
  const point = event.changedTouches?.[0];
  const started = touch;
  touch = null;
  if (!point) return;
  const dx = point.clientX - started.x;
  const dy = point.clientY - started.y;
  const elapsed = Date.now() - started.at;
  const distance = Math.max(Math.abs(dx), Math.abs(dy));
  if (distance >= SWIPE_MIN_PX && elapsed <= SWIPE_MAX_MS) {
    const direction = Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? "right" : "left") : dy > 0 ? "down" : "up";
    logEvent("swipe", { direction, distance: Math.round(distance), ms: elapsed, target: describeElement(started.target) });
  } else if (distance < 10 && elapsed >= LONG_PRESS_MS) {
    // Captains hold an occupied table to open its options.
    logEvent("long_press", { ms: elapsed, target: describeElement(started.target) });
  }
}

// ── page and network ────────────────────────────────────────────────────────────
export function connectionInfo() {
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  return {
    online: navigator.onLine,
    effective_type: connection?.effectiveType,
    rtt_ms: connection?.rtt,
    downlink_mbps: connection?.downlink,
  };
}

function onVisibility() {
  logEvent("visibility", { state: document.visibilityState });
  if (document.visibilityState === "hidden") flushLogs();
}

function onError(event) {
  logEvent("js_error", {
    message: String(event.message || event.error?.message || "error").slice(0, 500),
    source: redactUrl(event.filename),
    line: event.lineno,
    column: event.colno,
    stack: String(event.error?.stack || "").slice(0, 2000) || undefined,
  });
}

function onRejection(event) {
  const reason = event.reason;
  logEvent("unhandled_rejection", {
    message: String(reason?.message || reason || "rejection").slice(0, 500),
    stack: String(reason?.stack || "").slice(0, 2000) || undefined,
  });
}

export function installDomCapture() {
  if (installed || typeof document === "undefined") return uninstall;
  installed = true;
  const passive = { capture: true, passive: true };
  const onOnline = () => logEvent("network_status", connectionInfo());
  const onConnectionChange = () => logEvent("network_change", connectionInfo());
  const onPageHide = () => {
    logEvent("page_hide", {});
    flushLogs();
  };
  const connection = navigator.connection;
  const added = [
    [document, "click", onClick, true],
    [document, "change", onChange, true],
    [document, "submit", onSubmit, true],
    [document, "keydown", onKeyDown, true],
    [document, "scroll", onScroll, passive],
    [document, "touchstart", onTouchStart, passive],
    [document, "touchend", onTouchEnd, passive],
    [document, "visibilitychange", onVisibility, false],
    [window, "online", onOnline, false],
    [window, "offline", onOnline, false],
    [window, "error", onError, false],
    [window, "unhandledrejection", onRejection, false],
    [window, "pagehide", onPageHide, false],
  ];
  if (connection?.addEventListener) added.push([connection, "change", onConnectionChange, false]);
  added.forEach(([target, type, handler, options]) => target.addEventListener(type, handler, options));
  // Evidence the device was alive and what its network looked like, even on a
  // quiet screen.
  const heartbeat = setInterval(() => logEvent("heartbeat", connectionInfo()), HEARTBEAT_MS);
  uninstall = () => {
    added.forEach(([target, type, handler, options]) => target.removeEventListener(type, handler, options));
    clearInterval(heartbeat);
    scrolls.forEach(gesture => clearTimeout(gesture.timer));
    scrolls.clear();
    touch = null;
    installed = false;
    uninstall = () => {};
  };
  return uninstall;
}

